/**
 * Claude API Service for Text Editor
 * 
 * This service provides integration with Claude AI for text analysis and improvement.
 * It includes fallback mechanisms and error handling for robust operation.
 */

class ClaudeService {
  constructor() {
    this.isAvailable = this.checkAvailability();
  }

  /**
   * Check if Claude API is available
   */
  checkAvailability() {
    return typeof window !== 'undefined' && 
           window.claude && 
           typeof window.claude.complete === 'function';
  }

  /**
   * Complete text using Claude API
   */
  async complete(options) {
    if (!this.isAvailable) {
      throw new Error('Claude API is not available');
    }

    try {
      const response = await window.claude.complete({
        prompt: options.prompt,
        max_tokens: options.max_tokens || 1000,
        temperature: options.temperature || 0.7,
        ...options
      });

      return response;
    } catch (error) {
      console.error('Claude API error:', error);
      throw new Error(`Claude API request failed: ${error.message}`);
    }
  }

  /**
   * Analyze text for grammar, spelling, and style issues
   */
  async analyzeText(text, analysisType = 'comprehensive') {
    const prompts = {
      grammar: `Analyze the following text for grammar errors and provide specific corrections:

"${text}"

Return a JSON array where each item has:
- type: "grammar"
- original: the incorrect text
- suggestion: the corrected text
- explanation: why the change is needed
- position: approximate character position
- confidence: confidence score (0-1)`,

      spelling: `Check the following text for spelling errors:

"${text}"

Return a JSON array where each item has:
- type: "spelling"
- original: the misspelled word
- suggestion: the correct spelling
- explanation: brief explanation if needed
- position: approximate character position
- confidence: confidence score (0-1)`,

      style: `Analyze the following text for style improvements and clarity:

"${text}"

Return a JSON array where each item has:
- type: "style"
- original: the text that could be improved
- suggestion: the improved version
- explanation: why this improves the text
- position: approximate character position
- confidence: confidence score (0-1)`,

      comprehensive: `Comprehensively analyze the following text for grammar, spelling, punctuation, style, and clarity issues:

"${text}"

Return a JSON array where each item has:
- type: one of "grammar", "spelling", "punctuation", "style", "clarity"
- original: the text that needs improvement
- suggestion: the improved version
- explanation: detailed explanation of the improvement
- position: approximate character position
- confidence: confidence score (0-1)`
    };

    try {
      const response = await this.complete({
        prompt: prompts[analysisType] || prompts.comprehensive,
        max_tokens: 2000,
        temperature: 0.3
      });

      return this.parseSuggestions(response);
    } catch (error) {
      console.error('Text analysis error:', error);
      throw error;
    }
  }

  /**
   * Parse Claude response into structured suggestions
   */
  parseSuggestions(response) {
    try {
      // Try to extract JSON from the response
      const jsonMatch = response.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const suggestions = JSON.parse(jsonMatch[0]);
        return suggestions.map(suggestion => ({
          ...suggestion,
          confidence: suggestion.confidence || 0.8,
          position: suggestion.position || 0
        }));
      }
    } catch (parseError) {
      console.warn('Failed to parse Claude response as JSON:', parseError);
    }

    // Fallback: create a general suggestion from the response
    return [{
      type: 'general',
      original: '',
      suggestion: response,
      explanation: 'AI-generated improvement suggestion',
      position: 0,
      confidence: 0.7
    }];
  }

  /**
   * Improve text based on specific mode
   */
  async improveText(text, mode = 'improve', customPrompt = '') {
    const prompts = {
      improve: `Improve the following text for better clarity, flow, and impact while maintaining the original meaning:

"${text}"

Provide only the improved version.`,

      simplify: `Simplify the following text to make it easier to understand while keeping the core message:

"${text}"

Provide only the simplified version.`,

      formal: `Rewrite the following text in a more formal, professional tone:

"${text}"

Provide only the formal version.`,

      casual: `Rewrite the following text in a more casual, conversational tone:

"${text}"

Provide only the casual version.`,

      expand: `Expand the following text with more detail, context, and supporting information:

"${text}"

Provide only the expanded version.`,

      translate: `Translate the following text to Spanish:

"${text}"

Provide only the translation.`,

      custom: `${customPrompt}

"${text}"

Provide only the result.`
    };

    try {
      const response = await this.complete({
        prompt: prompts[mode] || prompts.improve,
        max_tokens: 1000,
        temperature: 0.7
      });

      return response.trim();
    } catch (error) {
      console.error('Text improvement error:', error);
      throw error;
    }
  }

  /**
   * Get writing suggestions for selected text
   */
  async getWritingSuggestions(text, context = '') {
    const prompt = `Provide writing suggestions for the following text${context ? ` in the context of: ${context}` : ''}:

"${text}"

Return a JSON array of suggestions where each has:
- type: type of suggestion (e.g., "clarity", "conciseness", "tone")
- suggestion: the improved text
- explanation: why this is better
- confidence: confidence score (0-1)`;

    try {
      const response = await this.complete({
        prompt,
        max_tokens: 1500,
        temperature: 0.5
      });

      return this.parseSuggestions(response);
    } catch (error) {
      console.error('Writing suggestions error:', error);
      throw error;
    }
  }

  /**
   * Generate text analytics
   */
  generateAnalytics(text, suggestions = []) {
    const words = text.split(/\s+/).filter(word => word.length > 0);
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const characters = text.length;
    
    // Calculate readability score (Flesch Reading Ease)
    const syllables = words.reduce((count, word) => count + this.countSyllables(word), 0);
    const avgWordsPerSentence = sentences.length > 0 ? words.length / sentences.length : 0;
    const avgSyllablesPerWord = words.length > 0 ? syllables / words.length : 0;
    
    let readabilityScore = 0;
    if (sentences.length > 0 && words.length > 0) {
      readabilityScore = 206.835 - (1.015 * avgWordsPerSentence) - (84.6 * avgSyllablesPerWord);
      readabilityScore = Math.max(0, Math.min(100, Math.round(readabilityScore)));
    }

    return {
      wordCount: words.length,
      characterCount: characters,
      sentenceCount: sentences.length,
      suggestionCount: suggestions.length,
      readabilityScore,
      avgWordsPerSentence: Math.round(avgWordsPerSentence),
      avgSyllablesPerWord: Math.round(avgSyllablesPerWord * 10) / 10,
      estimatedReadingTime: Math.ceil(words.length / 200) // 200 words per minute
    };
  }

  /**
   * Count syllables in a word (simplified algorithm)
   */
  countSyllables(word) {
    word = word.toLowerCase();
    if (word.length <= 3) return 1;
    
    word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '');
    word = word.replace(/^y/, '');
    
    const matches = word.match(/[aeiouy]{1,2}/g);
    return matches ? matches.length : 1;
  }

  /**
   * Mock Claude API for development/testing
   */
  static createMockAPI() {
    if (typeof window !== 'undefined' && !window.claude) {
      window.claude = {
        complete: async (options) => {
          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));
          
          // Mock responses based on prompt content
          const prompt = options.prompt.toLowerCase();
          
          if (prompt.includes('grammar')) {
            return JSON.stringify([
              {
                type: 'grammar',
                original: 'This are wrong',
                suggestion: 'This is wrong',
                explanation: 'Subject-verb agreement error',
                position: 0,
                confidence: 0.95
              }
            ]);
          }
          
          if (prompt.includes('spelling')) {
            return JSON.stringify([
              {
                type: 'spelling',
                original: 'recieve',
                suggestion: 'receive',
                explanation: 'Common spelling error - i before e except after c',
                position: 10,
                confidence: 0.99
              }
            ]);
          }
          
          if (prompt.includes('improve')) {
            return 'This is an improved version of your text with better clarity and flow.';
          }
          
          if (prompt.includes('simplify')) {
            return 'This is a simpler version that is easier to understand.';
          }
          
          // Default comprehensive analysis
          return JSON.stringify([
            {
              type: 'style',
              original: 'very good',
              suggestion: 'excellent',
              explanation: 'More precise and impactful word choice',
              position: 5,
              confidence: 0.8
            },
            {
              type: 'clarity',
              original: 'in order to',
              suggestion: 'to',
              explanation: 'Removes unnecessary words for better conciseness',
              position: 15,
              confidence: 0.9
            }
          ]);
        }
      };
    }
  }
}

// Create and export singleton instance
const claudeService = new ClaudeService();

// Initialize mock API in development or when Claude is not available
if (process.env.NODE_ENV === 'development' || !claudeService.isAvailable) {
  ClaudeService.createMockAPI();
  // Re-check availability after creating mock
  claudeService.isAvailable = claudeService.checkAvailability();
}

export default claudeService;
